import React, { useState, useMemo, useEffect } from 'react';
import { RightHandFinger, RightHandPart, ALL_RIGHT_FINGERS, getFingerDisplayName, FingerType } from '../types';
import { 
  RIGHT_IMAGE_URLS, 
  RIGHT_FINGER_KEY_MAP, 
  RIGHT_KEY_FINGER_MAP,
  RIGHT_HAND_PART_SVG_PATHS,
  RIGHT_FINGER_POSITION_ADJUSTMENTS,
  RIGHT_DEBUG_FINGER_AREAS,
  RIGHT_LETTER_POSITION_ADJUSTMENTS,
  RIGHT_LETTER_FONT_SETTINGS,
  RIGHT_DEBUG_LETTER_POSITIONS
} from '../rightHandConstants';

// Common image style (bypasses Tailwind) - FORCED EXACT SIZING
const imageLayerStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '507px',  // FORCED to match container
  height: '580px', // FORCED to match container
  objectFit: 'fill', // CHANGED from 'contain' to 'fill' for exact sizing
  pointerEvents: 'none',
  display: 'block'
};

interface RightHandPickerProps {
  selectedFingers?: FingerType[];
  onChange?: (fingers: FingerType[]) => void;
  disabled?: boolean;
  className?: string;
  mode?: 'active' | 'demonstration';
  externalFingerData?: RightHandFinger[];
  externalHandData?: 'full' | 'none';
  onModeChange?: (mode: 'active' | 'demonstration') => void;
}

const RightHandPicker: React.FC<RightHandPickerProps> = ({
  selectedFingers: externalSelectedFingers = [],
  onChange,
  disabled = false,
  className = '',
  mode = 'active',
  externalFingerData = [],
  externalHandData = 'none'
}) => {
  const [hoveredPart, setHoveredPart] = useState<RightHandPart | null>(null);
  const [selectedFingers, setSelectedFingers] = useState<Set<RightHandFinger>>(new Set());
  
  const isHandFloating = useMemo(() => {
    const hasHoverInteraction = hoveredPart !== null;
    const hasSelectedFingers = selectedFingers.size > 0;
    const isDisabledState = disabled;
    const hasExternalDemoData = mode === 'demonstration' && (externalFingerData.length > 0 || externalHandData === 'full');
    return !hasHoverInteraction && !hasSelectedFingers && !isDisabledState && !hasExternalDemoData;
  }, [hoveredPart, selectedFingers, disabled, mode, externalFingerData, externalHandData]);
  
  useEffect(() => {
    if (mode === 'active') {
      const rightHandFingers = externalSelectedFingers.filter(f => f.startsWith('right')) as RightHandFinger[];
      setSelectedFingers(new Set(rightHandFingers));
    }
  }, [externalSelectedFingers, mode]);
  
  useEffect(() => {
    if (mode === 'demonstration') {
      let demonstrationFingers = new Set<RightHandFinger>();
      if (externalHandData === 'full') {
        demonstrationFingers = new Set(ALL_RIGHT_FINGERS);
      } else if (externalFingerData.length > 0) {
        demonstrationFingers = new Set(externalFingerData);
      }
      setSelectedFingers(demonstrationFingers);
    }
  }, [mode, externalFingerData, externalHandData]);

  const areAllFingersSelected = useMemo(() => selectedFingers.size === ALL_RIGHT_FINGERS.length, [selectedFingers]);
  
  const fireFingerSelectionChange = (newSelectedFingers: Set<RightHandFinger>) => {
    if (onChange) onChange(Array.from(newSelectedFingers) as FingerType[]);
  };

  const handlePartClick = (part: RightHandPart) => {
    if (disabled || mode === 'demonstration') return;
    
    let newSelectedFingers: Set<RightHandFinger>;
    if (part === RightHandPart.PALM) {
      newSelectedFingers = areAllFingersSelected ? new Set() : new Set(ALL_RIGHT_FINGERS);
    } else {
      newSelectedFingers = new Set(selectedFingers);
      const fingerValue = part as RightHandFinger;
      if (areAllFingersSelected) {
        newSelectedFingers = new Set([fingerValue]);
      } else {
        newSelectedFingers.has(fingerValue) ? newSelectedFingers.delete(fingerValue) : newSelectedFingers.add(fingerValue);
      }
    }
    setSelectedFingers(newSelectedFingers);
    fireFingerSelectionChange(newSelectedFingers);
  };

  const handlePartMouseEnter = (part: RightHandPart) => {
    if (mode !== 'demonstration') setHoveredPart(part);
  };

  const handlePartMouseLeave = () => {
    if (mode !== 'demonstration') setHoveredPart(null);
  };
  
  const isFingerActive = (finger: RightHandFinger) => selectedFingers.has(finger) || hoveredPart === finger;
  const isPalmActive = () => areAllFingersSelected || hoveredPart === RightHandPart.PALM;
  
  const activeKeys = useMemo(() => {
    const keys = new Set<string>();
    selectedFingers.forEach(finger => RIGHT_FINGER_KEY_MAP[finger].forEach(key => keys.add(key)));
    if (hoveredPart && hoveredPart !== RightHandPart.PALM) {
      const hoveredFinger = hoveredPart as RightHandFinger;
      RIGHT_FINGER_KEY_MAP[hoveredFinger].forEach(key => keys.add(key));
    }
    if(hoveredPart === RightHandPart.PALM){
      ALL_RIGHT_FINGERS.forEach(finger => RIGHT_FINGER_KEY_MAP[finger].forEach(key => keys.add(key)));
    }
    return keys;
  }, [selectedFingers, hoveredPart]);

  return (
    <div 
      style={{
        position: 'relative',
        width: '507px',
        height: '580px',
        userSelect: 'none'
      }}
      className={className}
    >
      {/* Debug Panel */}
      {(RIGHT_DEBUG_FINGER_AREAS || RIGHT_DEBUG_LETTER_POSITIONS) && (
        <div style={{position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.8)', color: 'white', padding: '8px', borderRadius: '4px', fontSize: '12px', zIndex: 50}}>
          <div style={{fontWeight: 'bold', marginBottom: '4px'}}>🐛 DEBUG MODE (RIGHT)</div>
          {RIGHT_DEBUG_FINGER_AREAS && <div style={{color: '#fca5a5'}}>🔴 Finger Areas: ON</div>}
          {RIGHT_DEBUG_LETTER_POSITIONS && <div style={{color: '#86efac'}}>🟢 Letter Positions: ON</div>}
        </div>
      )}
      
      {/* Layer 1: Keyboard Base */}
      <img 
        src={RIGHT_IMAGE_URLS.KEYBOARD_BASE} 
        alt="Right Keyboard Base" 
        style={imageLayerStyle}
      />

      {/* Layer 2: Active Keys */}
      {Object.keys(RIGHT_KEY_FINGER_MAP).map((key) => (
        <img
          key={key}
          src={RIGHT_IMAGE_URLS.KEYS[key as keyof typeof RIGHT_IMAGE_URLS.KEYS]}
          alt={`${key} active`}
          style={{
            ...imageLayerStyle,
            opacity: activeKeys.has(key) ? 1 : 0,
            transition: 'opacity 200ms'
          }}
        />
      ))}
      
      {/* Layer 3: Key Letters */}
      {Object.entries(RIGHT_LETTER_POSITION_ADJUSTMENTS).map(([letter, pos]) => {
        const offsetX = pos.offsetX || '0px';
        const offsetY = pos.offsetY || '0px';
        return (
          <span
            key={letter}
            style={{
              position: 'absolute',
              top: pos.top,
              left: pos.left,
              transform: `translate(-50%, -50%) translate(${offsetX}, ${offsetY})`,
              pointerEvents: 'none',
              userSelect: 'none',
              fontSize: RIGHT_LETTER_FONT_SETTINGS.fontSize,
              fontFamily: RIGHT_LETTER_FONT_SETTINGS.fontFamily,
              fontWeight: RIGHT_LETTER_FONT_SETTINGS.fontWeight,
              color: RIGHT_LETTER_FONT_SETTINGS.color,
              textShadow: RIGHT_LETTER_FONT_SETTINGS.textShadow,
              letterSpacing: RIGHT_LETTER_FONT_SETTINGS.letterSpacing,
              ...(RIGHT_DEBUG_LETTER_POSITIONS ? {
                backgroundColor: 'rgba(0, 255, 0, 0.3)',
                border: '1px solid lime',
                padding: '2px 4px',
                borderRadius: '3px',
              } : {}),
            }}
          >
            {letter === 'SPACE' ? '' : letter}
          </span>
        );
      })}

      {/* Animated Hand Container */}
      <div 
        className={isHandFloating ? 'animate-hand-soaring' : ''}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          transition: 'transform 500ms ease-out',
          // @ts-ignore - CSS variables
          '--float-duration': '4.5s',
          '--float-amplitude': '8px',
          '--float-rotation': '0.5deg',
          '--float-scale': '1.01',
        }}
      >
        {/* Layer 4: Inactive Hand */}
        <img 
          src={RIGHT_IMAGE_URLS.RIGHT_PALM_INACTIVE} 
          alt="Inactive Right Hand" 
          style={imageLayerStyle}
        />
      
        {/* Layer 5: Active Palm */}
        {isPalmActive() && (
          <img 
            src={RIGHT_IMAGE_URLS.RIGHT_PALM_ACTIVE} 
            alt="Active Right Palm" 
            style={{
              ...imageLayerStyle,
              transition: 'opacity 200ms'
            }}
          />
        )}
        
        {/* Layer 5: Active Fingers */}
        {!isPalmActive() && ALL_RIGHT_FINGERS.map((finger) => {
          const getImageKey = (finger: RightHandFinger): keyof typeof RIGHT_IMAGE_URLS => {
            switch (finger) {
              case 'rightPinky': return 'RIGHT_PINKY_ACTIVE';
              case 'rightRing': return 'RIGHT_RING_ACTIVE';
              case 'rightMiddle': return 'RIGHT_MIDDLE_ACTIVE';
              case 'rightPointing': return 'RIGHT_INDEX_ACTIVE';
              case 'rightThumb': return 'RIGHT_THUMB_ACTIVE';
              default: return 'RIGHT_PINKY_ACTIVE';
            }
          };
          
          const imageKey = getImageKey(finger);
          return (
            <img
              key={finger}
              src={RIGHT_IMAGE_URLS[imageKey] as string}
              alt={`${getFingerDisplayName(finger)} active`}
              style={{
                ...imageLayerStyle,
                opacity: isFingerActive(finger) ? 1 : 0,
                transition: 'opacity 200ms'
              }}
            />
          );
        })}

        {/* Layer 6: SVG Hotspots */}
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%'
          }}
          viewBox="0 0 1138 1159"
          fill="transparent"
          xmlns="http://www.w3.org/2000/svg"
        >
          {Object.entries(RIGHT_HAND_PART_SVG_PATHS).map(([part, pathData]) => {
            const adjustments = RIGHT_FINGER_POSITION_ADJUSTMENTS[part as RightHandPart];
            const transform = `translate(${adjustments.translateX}, ${adjustments.translateY}) scale(${adjustments.scaleX || 1}, ${adjustments.scaleY || 1})`;
            
            return (
              <g key={part} transform={transform}>
                <path
                  d={pathData}
                  onClick={() => handlePartClick(part as RightHandPart)}
                  onMouseEnter={() => handlePartMouseEnter(part as RightHandPart)}
                  onMouseLeave={handlePartMouseLeave}
                  stroke="transparent"
                  style={{
                    cursor: mode === 'demonstration' ? 'default' : disabled ? 'not-allowed' : 'pointer',
                    fill: RIGHT_DEBUG_FINGER_AREAS ? 'rgba(255, 0, 0, 0.3)' : 'transparent'
                  }}
                />
              </g>
            );  
          })}
        </svg>
      </div>
    </div>
  );
};

export default RightHandPicker;
